import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { useMessages } from '../components/Messages/contexts/MessagesContext';
import { useTickets } from 'contexts/TicketsContext';
import { GetTicketsRequestParamsDTO, GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { TicketDetailsI } from 'declarations/interfaces/tickets';
import { NoopT } from 'declarations/types/common';
import { useFetchTicket } from 'hooks/api/useTickets';

type UseActionsMenuT = {
  ticketDetails: TicketDetailsI | null;
  isTicketDetailsFetchingError: boolean;
  isLoadingTicketDetails: boolean;
  isTicketReopenPopupOpened: boolean;
  isTicketApprovePopupOpened: boolean;
  isTicketClosePopupOpened: boolean;
  isAddAttachmentPopupOpened: boolean;
  isViewAttachmentsPopupOpened: boolean;
  isChangeClaimStatusPopupOpened: boolean;
  isChangePriorityPopupOpened: boolean;
  isDeleteTicketPopupOpened: boolean;
  selectedTicketId: string | null;
  setTicketId: (id: string | null) => void;
  fetchTicketsParams: GetTicketsRequestParamsDTO;
  handleOpenViewAttachmentsPopup: NoopT;
  handleCloseViewAttachmentsPopup: NoopT;
  handleOpenAddAttachmentPopup: NoopT;
  handleCloseAddAttachmentPopup: NoopT;
  handleOpenTicketReopenPopup: NoopT;
  handleCloseTicketReopenPopup: NoopT;
  handleCloseTicketDetailsPopup: NoopT;
  handleOpenTicketApprovePopup: NoopT;
  handleCloseTicketApprovePopup: NoopT;
  handleOpenTicketClosePopup: NoopT;
  handleCloseTicketClosePopup: NoopT;
  handleOpenClaimStatusChangePopup: NoopT;
  handleCloseClaimStatusChangePopup: NoopT;
  handleOpenPriorityChangePopup: NoopT;
  handleClosePriorityChangePopup: NoopT;
  handleOpenDeleteTicketPopup: NoopT;
  handleCloseDeleteTicketPopup: NoopT;
};

const useTicketReopenApprovalPopup = (): UseActionsMenuT => {
  /**
   * Hooks
   */
  const [isTicketReopenPopupOpened, setTicketReopenPopupVisibility] = useState(false);
  const [isTicketApprovePopupOpened, setTicketApprovePopupVisibility] = useState(false);
  const [isTicketClosePopupOpened, setTicketClosePopupVisibility] = useState(false);
  const [isAddAttachmentPopupOpened, setAddAttachmentPopupVisibility] = useState(false);
  const [isViewAttachmentsPopupOpened, setViewAttachmentsPopupVisibility] = useState(false);
  const [isChangeClaimStatusPopupOpened, setChangeClaimStatusPopupVisibility] = useState(false);
  const [isChangePriorityPopupOpened, setChangePriorityPopupVisibility] = useState(false);
  const [isDeleteTicketPopupOpened, setDeleteTicketPopupVisibility] = useState(false);
  const { setTicketId, selectedTicketId, fetchTicketsParams } = useTickets();
  const { debouncedCreateTicketMessage, unsavedMessage } = useMessages();
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const ticketDetailsResult = useFetchTicket({
    ticketId: selectedTicketId || '',
    onSuccess: updatedTicket => {
      queryClient.setQueryData<InfiniteData<GetTicketsResponseDTO>>(
        [RequestQueryKeysE.FetchTickets, fetchTicketsParams],
        previousData => {
          return previousData
            ? {
                pageParams: previousData.pageParams,
                pages: previousData.pages.map(page => ({
                  data: page.data.map(ticket => {
                    if (ticket.id === updatedTicket.id && !ticket.isViewed) {
                      return {
                        ...ticket,
                        isViewed: true,
                      };
                    }

                    return ticket;
                  }),
                  count: page.count,
                })),
              }
            : undefined;
        }
      );
    },
  });

  /**
   * Handlers
   */
  const handleCloseTicketDetailsPopup = useCallback(() => {
    setTicketId(null);

    if (unsavedMessage) {
      debouncedCreateTicketMessage.flush();
    }
  }, [setTicketId, unsavedMessage, debouncedCreateTicketMessage]);
  const handleOpenTicketReopenPopup = useCallback(() => {
    setTicketReopenPopupVisibility(true);
  }, []);
  const handleCloseTicketReopenPopup = useCallback(() => {
    setTicketReopenPopupVisibility(false);
  }, []);
  const handleOpenTicketApprovePopup = useCallback(() => {
    setTicketApprovePopupVisibility(true);
  }, []);
  const handleCloseTicketApprovePopup = useCallback(() => {
    setTicketApprovePopupVisibility(false);
  }, []);
  const handleOpenTicketClosePopup = useCallback(() => {
    setTicketClosePopupVisibility(true);
  }, []);
  const handleCloseTicketClosePopup = useCallback(() => {
    setTicketClosePopupVisibility(false);
    queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
  }, [queryClient]);
  const handleOpenAddAttachmentPopup = useCallback(() => {
    setAddAttachmentPopupVisibility(true);
  }, []);
  const handleCloseAddAttachmentPopup = useCallback(() => {
    setAddAttachmentPopupVisibility(false);
  }, []);
  const handleOpenViewAttachmentsPopup = useCallback(() => {
    setViewAttachmentsPopupVisibility(true);
  }, []);
  const handleCloseViewAttachmentsPopup = useCallback(() => {
    setViewAttachmentsPopupVisibility(false);
  }, []);
  const handleOpenClaimStatusChangePopup = useCallback(() => {
    setChangeClaimStatusPopupVisibility(true);
  }, []);
  const handleCloseClaimStatusChangePopup = useCallback(() => {
    setChangeClaimStatusPopupVisibility(false);
  }, []);
  const handleOpenPriorityChangePopup = useCallback(() => {
    setChangePriorityPopupVisibility(true);
  }, []);
  const handleClosePriorityChangePopup = useCallback(() => {
    setChangePriorityPopupVisibility(false);
  }, []);
  const handleOpenDeleteTicketPopup = useCallback(() => {
    setDeleteTicketPopupVisibility(true);
  }, []);
  const handleCloseDeleteTicketPopup = useCallback(() => {
    setDeleteTicketPopupVisibility(false);
  }, []);

  return {
    ticketDetails: ticketDetailsResult.data ?? null,
    isTicketDetailsFetchingError: ticketDetailsResult.isError,
    isLoadingTicketDetails: ticketDetailsResult.isLoading || ticketDetailsResult.isRefetching,
    selectedTicketId,
    setTicketId,
    fetchTicketsParams,
    isTicketReopenPopupOpened,
    handleCloseTicketDetailsPopup,
    handleOpenTicketReopenPopup,
    handleCloseTicketReopenPopup,
    isTicketApprovePopupOpened,
    handleOpenTicketApprovePopup,
    handleCloseTicketApprovePopup,
    isTicketClosePopupOpened,
    handleOpenTicketClosePopup,
    handleCloseTicketClosePopup,
    isAddAttachmentPopupOpened,
    handleOpenAddAttachmentPopup,
    handleCloseAddAttachmentPopup,
    isViewAttachmentsPopupOpened,
    handleOpenViewAttachmentsPopup,
    handleCloseViewAttachmentsPopup,
    isChangeClaimStatusPopupOpened,
    handleOpenClaimStatusChangePopup,
    handleCloseClaimStatusChangePopup,
    isChangePriorityPopupOpened,
    handleOpenPriorityChangePopup,
    handleClosePriorityChangePopup,
    isDeleteTicketPopupOpened,
    handleOpenDeleteTicketPopup,
    handleCloseDeleteTicketPopup,
  };
};

export default useTicketReopenApprovalPopup;
